import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Home, Building2, ArrowRight, ArrowLeft, CheckCircle, Phone, Mail, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { sendLeadSubmissionEvent } from '../../utils/postMessageBridge';

const ModalContactForm = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [errors, setErrors] = useState({});
  const [modalTopPosition, setModalTopPosition] = useState(80);
  const { toast } = useToast();
  
  // CAPTCHA State
  const [captchaNumbers, setCaptchaNumbers] = useState({ a: 0, b: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [honeypot, setHoneypot] = useState(''); // Spam-Trap

  const [formData, setFormData] = useState({
    // Schritt 1: Grunddaten + Kontakt
    propertyType: '',
    salutation: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    acceptPrivacy: false,
    
    // Schritt 2: Immobiliendetails + Heizung & Projektziel (optional)
    buildingYear: '',
    livingSpace: '',
    currentHeating: '',
    heatingAge: '',
    annualCosts: '',
    projectGoal: '',
    timeline: '',
    budget: '',
    
    // Schritt 3: Installationsadresse
    street: '',
    postalCode: '',
    city: ''
  });

  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  // Reset form when modal opens + scroll to top (nur Desktop)
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setIsCompleted(false);
      setErrors({});
      setCaptchaAnswer('');
      setHoneypot('');
      // Generate new CAPTCHA numbers
      setCaptchaNumbers({
        a: Math.floor(Math.random() * 10) + 1,
        b: Math.floor(Math.random() * 10) + 1
      });
      
      // Nur Desktop: Scroll zum Seitenanfang
      const isMobile = window.innerWidth < 768;
      if (!isMobile) {
        try {
          window.parent.postMessage({ type: 'SCROLL_TO_TOP' }, '*');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
          console.log('Scroll failed:', e);
        }
      }
      
      // Modal Position
      setModalTopPosition(80);
    }
  }, [isOpen]);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Funktion zum Extrahieren aller UTM-Parameter aus der URL
  const getUTMParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {};
    
    for (const [key, value] of urlParams.entries()) {
      if (key.toLowerCase().startsWith('utm_') || 
          ['source', 'medium', 'campaign', 'term', 'content', 'gclid', 'fbclid'].includes(key.toLowerCase())) {
        utmParams[key] = value;
      }
    }
    
    return utmParams;
  };

  // Funktion zum Extrahieren der Tracking-Daten
  const getTrackingData = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_term: urlParams.get('utm_term') || null,
      utm_content: urlParams.get('utm_content') || null,
      gclid: urlParams.get('gclid') || null,
      gbraid: urlParams.get('gbraid') || null,
      fbclid: urlParams.get('fbclid') || null,
      campaignid: urlParams.get('campaignid') || null,
      adgroupid: urlParams.get('adgroupid') || null,
      referrer: document.referrer || null,
      landing_page: window.location.href
    };
  };

  // Funktion zum Öffnen der Bestätigungsseite mit UTM-Parametern
  const openConfirmationPage = () => {
    const baseUrl = 'https://www.solartechnik-lauckner.de/bestaetigungsseite-waermepumpen';
    const utmParams = getUTMParameters();
    const queryString = new URLSearchParams(utmParams).toString();
    const finalUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    window.open(finalUrl, '_blank');
  };

  const saveStepData = async (stepNumber) => {
    setIsSubmitting(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      let stepData = {};
      
      switch (stepNumber) {
        case 1:
          const trackingData = getTrackingData();
          stepData = {
            step: 1,
            leadId: leadId,
            propertyType: formData.propertyType,
            salutation: formData.salutation,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            acceptPrivacy: formData.acceptPrivacy,
            ...trackingData
          };
          break;
        case 2:
          stepData = {
            step: 2,
            leadId: leadId,
            email: formData.email,
            buildingYear: formData.buildingYear,
            livingSpace: formData.livingSpace,
            currentHeating: formData.currentHeating,
            heatingAge: formData.heatingAge,
            annualCosts: formData.annualCosts,
            projectGoal: formData.projectGoal,
            timeline: formData.timeline,
            budget: formData.budget
          };
          break;
        case 3:
          stepData = {
            step: 3,
            leadId: leadId,
            email: formData.email,
            street: formData.street,
            postalCode: formData.postalCode,
            city: formData.city
          };
          break;
        default:
          return;
      }

      const response = await fetch(`${backendUrl}/api/leads/step`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(stepData)
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        if (stepNumber === 1) {
          setLeadId(result.leadId);
        }
        
        if (stepNumber === 3) {
          setIsCompleted(true);
          
          sendLeadSubmissionEvent('modal-contact', {
            leadId: result.leadId,
            email: formData.email
          });
          
          openConfirmationPage();
          
          toast({
            title: "Erfolgreich abgesendet!",
            description: "Vielen Dank! Unser Experte meldet sich innerhalb von 24 Stunden bei Ihnen.",
            variant: "default"
          });
        } else {
          toast({
            title: "Schritt gespeichert",
            description: `Schritt ${stepNumber} erfolgreich gespeichert.`,
            variant: "default"
          });
        }
        
        return true;
      } else {
        throw new Error(result.message || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving step:', error);
      toast({
        title: "Fehler",
        description: error.message || "Daten konnten nicht gespeichert werden.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = async () => {
    if (!validateCurrentStep()) {
      return;
    }

    const saved = await saveStepData(currentStep);
    if (saved && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    
    switch (currentStep) {
      case 1:
        if (!formData.propertyType) newErrors.propertyType = true;
        if (!formData.salutation) newErrors.salutation = true;
        if (!formData.firstName) newErrors.firstName = true;
        if (!formData.lastName) newErrors.lastName = true;
        if (!formData.email) newErrors.email = true;
        if (!formData.phone) newErrors.phone = true;
        if (!formData.acceptPrivacy) newErrors.acceptPrivacy = true;
        
        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          toast({
            title: "Pflichtfelder fehlen",
            description: "Bitte füllen Sie alle rot markierten Felder aus.",
            variant: "destructive"
          });
          return false;
        }
        break;
      case 3:
        // Honeypot check - if filled, it's a bot
        if (honeypot) {
          console.log('Bot detected via honeypot');
          return false;
        }
        
        if (!formData.street) newErrors.street = true;
        if (!formData.postalCode) newErrors.postalCode = true;
        if (!formData.city) newErrors.city = true;
        
        // CAPTCHA validation
        const correctAnswer = captchaNumbers.a + captchaNumbers.b;
        if (parseInt(captchaAnswer) !== correctAnswer) {
          newErrors.captcha = true;
          toast({
            title: "Sicherheitsprüfung fehlgeschlagen",
            description: "Bitte lösen Sie die Rechenaufgabe korrekt.",
            variant: "destructive"
          });
          // Generate new CAPTCHA
          setCaptchaNumbers({
            a: Math.floor(Math.random() * 10) + 1,
            b: Math.floor(Math.random() * 10) + 1
          });
          setCaptchaAnswer('');
          return false;
        }
        
        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          toast({
            title: "Adresse erforderlich",
            description: "Bitte füllen Sie alle rot markierten Adressfelder aus.",
            variant: "destructive"
          });
          return false;
        }
        break;
      default:
        return true;
    }
    
    setErrors({});
    return true;
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - fixed über gesamte Seite */}
      <div 
        className="fixed inset-0 z-50 bg-black/50 md:block"
        onClick={onClose}
      />
      
      {/* Modal - Vollbild auf Mobile, Modal auf Desktop */}
      <div 
        className="fixed z-[51] inset-0 md:inset-auto md:right-4 bg-white md:rounded-2xl shadow-2xl w-full md:max-w-2xl overflow-y-auto"
        style={{ 
          top: window.innerWidth >= 768 ? `${modalTopPosition}px` : 0,
          maxWidth: window.innerWidth >= 768 ? '42rem' : '100%',
          maxHeight: window.innerWidth >= 768 ? '85vh' : '100%',
          borderRadius: window.innerWidth >= 768 ? '1rem' : 0
        }}
      >
        <Card className="border-0 shadow-none">
          <CardHeader className="relative pb-4 sticky top-0 bg-white z-10 border-b md:border-b-0">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Schließen"
            >
              <X className="w-5 h-5" />
            </button>
            
            <CardTitle className="text-xl md:text-2xl font-bold text-gray-900 pr-10">
              {isCompleted ? 'Vielen Dank!' : `Schritt ${currentStep} von ${totalSteps}`}
            </CardTitle>
            
            {!isCompleted && (
              <div className="mt-4">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-gray-500 mt-2">
                  {currentStep === 1 && 'Grunddaten & Kontakt'}
                  {currentStep === 2 && 'Immobiliendetails (optional)'}
                  {currentStep === 3 && 'Installationsadresse'}
                </p>
              </div>
            )}
          </CardHeader>

          <CardContent>
            {isCompleted ? (
              <div className="text-center py-12">
                <CheckCircle className="w-20 h-20 text-brand-green mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Ihre Anfrage wurde erfolgreich übermittelt!
                </h3>
                <p className="text-gray-600 mb-8">
                  Unser Experte wird sich innerhalb von 24 Stunden bei Ihnen melden.
                </p>
                <Button 
                  onClick={onClose}
                  className="bg-brand-green hover:bg-brand-green-hover text-white"
                >
                  Schließen
                </Button>
              </div>
            ) : (
              <form className="space-y-6">
                {/* Schritt 1 */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-lg font-semibold mb-4 block">Art der Immobilie {errors.propertyType && <span className="text-red-500">*</span>}</Label>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div 
                          className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                            formData.propertyType === 'einfamilienhaus' 
                              ? 'border-brand-green bg-brand-green/10' 
                              : errors.propertyType 
                              ? 'border-red-500 hover:border-red-600' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleInputChange('propertyType', 'einfamilienhaus')}
                        >
                          <div className="flex items-center space-x-3">
                            <Home className="w-8 h-8 text-brand-green" />
                            <div>
                              <div className="font-semibold">Einfamilienhaus</div>
                              <div className="text-sm text-gray-600">Freistehend oder Reihenhaus</div>
                            </div>
                          </div>
                        </div>
                        
                        <div 
                          className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                            formData.propertyType === 'mehrfamilienhaus' 
                              ? 'border-brand-green bg-brand-green/10' 
                              : errors.propertyType 
                              ? 'border-red-500 hover:border-red-600' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleInputChange('propertyType', 'mehrfamilienhaus')}
                        >
                          <div className="flex items-center space-x-3">
                            <Building2 className="w-8 h-8 text-brand-green-light" />
                            <div>
                              <div className="font-semibold">Mehrfamilienhaus</div>
                              <div className="text-sm text-gray-600">Mehrere Wohneinheiten</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="salutation">Anrede <span className="text-red-500">*</span></Label>
                      <Select onValueChange={(value) => handleInputChange('salutation', value)}>
                        <SelectTrigger className={`mt-1 ${errors.salutation ? 'border-red-500 border-2' : ''}`}>
                          <SelectValue placeholder="Bitte wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Herr">Herr</SelectItem>
                          <SelectItem value="Frau">Frau</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">Vorname *</Label>
                        <Input
                          id="firstName"
                          type="text"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className={`mt-1 ${errors.firstName ? 'border-red-500 border-2' : ''}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Nachname *</Label>
                        <Input
                          id="lastName"
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className={`mt-1 ${errors.lastName ? 'border-red-500 border-2' : ''}`}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">E-Mail *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className={`mt-1 ${errors.email ? 'border-red-500 border-2' : ''}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Handynummer *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className={`mt-1 ${errors.phone ? 'border-red-500 border-2' : ''}`}
                        />
                      </div>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="privacy"
                        checked={formData.acceptPrivacy}
                        onCheckedChange={(checked) => handleInputChange('acceptPrivacy', checked)}
                      />
                      <label htmlFor="privacy" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                        Ich stimme der <a href="/datenschutz" className="text-brand-green underline">Datenschutzerklärung</a> zu und bin damit einverstanden, dass meine Daten zur Kontaktaufnahme gespeichert werden. *
                      </label>
                    </div>
                  </div>
                )}

                {/* Schritt 2 */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
                      <p className="text-sm text-green-800">
                        <span className="font-semibold">✓ Schritt 2 ist optional!</span> Diese Angaben helfen uns, Sie besser zu beraten. Sie können diesen Schritt auch überspringen und direkt zur Adresseingabe gehen.
                      </p>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Immobiliendetails <span className="text-gray-400 font-normal text-base">(optional)</span></h3>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="buildingYear" className="text-gray-600">Baujahr <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <Input
                          id="buildingYear"
                          type="text"
                          placeholder="z.B. 2010"
                          value={formData.buildingYear}
                          onChange={(e) => handleInputChange('buildingYear', e.target.value)}
                          className="mt-1 border-gray-300"
                        />
                      </div>
                      <div>
                        <Label htmlFor="livingSpace" className="text-gray-600">Wohnfläche in m² <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <Input
                          id="livingSpace"
                          type="text"
                          placeholder="z.B. 150"
                          value={formData.livingSpace}
                          onChange={(e) => handleInputChange('livingSpace', e.target.value)}
                          className="mt-1 border-gray-300"
                        />
                      </div>
                    </div>

                    <hr className="my-6" />

                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="optional-details" className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-semibold text-gray-800">Weitere Details zur Heizung & Ihrem Projekt</span>
                            <span className="text-sm text-gray-500 font-normal">(optional - aufklappen für mehr Optionen)</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4 space-y-6">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <p className="text-sm text-blue-800">
                              <span className="font-semibold">💡 Hinweis:</span> Diese Angaben helfen uns, Ihnen ein noch genaueres Angebot zu erstellen. Sie können diese Felder auch leer lassen.
                            </p>
                          </div>
                    
                    <div>
                        <Label htmlFor="currentHeating" className="text-gray-600">Aktuelle Heizung <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <Select onValueChange={(value) => handleInputChange('currentHeating', value)}>
                          <SelectTrigger className="mt-1 border-gray-300">
                            <SelectValue placeholder="Optional" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gas">Gasheizung</SelectItem>
                            <SelectItem value="oil">Ölheizung</SelectItem>
                            <SelectItem value="electric">Elektroheizung</SelectItem>
                            <SelectItem value="heat-pump">Wärmepumpe</SelectItem>
                            <SelectItem value="pellet">Pelletheizung</SelectItem>
                            <SelectItem value="other">Sonstiges</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="heatingAge" className="text-gray-600">Alter der Heizung <span className="text-gray-400 font-normal">(optional)</span></Label>
                          <Select onValueChange={(value) => handleInputChange('heatingAge', value)}>
                            <SelectTrigger className="mt-1 border-gray-300">
                              <SelectValue placeholder="Optional" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0-5">0-5 Jahre</SelectItem>
                              <SelectItem value="6-10">6-10 Jahre</SelectItem>
                              <SelectItem value="11-15">11-15 Jahre</SelectItem>
                              <SelectItem value="16-20">16-20 Jahre</SelectItem>
                              <SelectItem value="20+">Über 20 Jahre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="annualCosts" className="text-gray-600">Jährliche Heizkosten <span className="text-gray-400 font-normal">(optional)</span></Label>
                          <Select onValueChange={(value) => handleInputChange('annualCosts', value)}>
                            <SelectTrigger className="mt-1 border-gray-300">
                              <SelectValue placeholder="Optional" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="under-1000">Unter 1.000€</SelectItem>
                              <SelectItem value="1000-2000">1.000€ - 2.000€</SelectItem>
                              <SelectItem value="2000-3000">2.000€ - 3.000€</SelectItem>
                              <SelectItem value="over-3000">Über 3.000€</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="projectGoal" className="text-gray-600">Ihr Projektziel <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <Select onValueChange={(value) => handleInputChange('projectGoal', value)}>
                          <SelectTrigger className="mt-1 border-gray-300">
                            <SelectValue placeholder="Optional" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cost-reduction">Kostensenkung</SelectItem>
                            <SelectItem value="co2-reduction">CO2-Reduktion</SelectItem>
                            <SelectItem value="modernization">Modernisierung</SelectItem>
                            <SelectItem value="independence">Unabhängigkeit</SelectItem>
                            <SelectItem value="combination">Kombination mehrerer Ziele</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="timeline" className="text-gray-600">Geplanter Umsetzungszeitpunkt <span className="text-gray-400 font-normal">(optional)</span></Label>
                          <Select onValueChange={(value) => handleInputChange('timeline', value)}>
                            <SelectTrigger className="mt-1 border-gray-300">
                              <SelectValue placeholder="Optional" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="asap">So schnell wie möglich</SelectItem>
                              <SelectItem value="3-months">In den nächsten 3 Monaten</SelectItem>
                              <SelectItem value="6-months">In den nächsten 6 Monaten</SelectItem>
                              <SelectItem value="next-year">Nächstes Jahr</SelectItem>
                              <SelectItem value="info-only">Nur Information</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                        <Label htmlFor="budget" className="text-gray-600">Geplantes Budget (inkl. Förderung) <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <Select onValueChange={(value) => handleInputChange('budget', value)}>
                          <SelectTrigger className="mt-1 border-gray-300">
                            <SelectValue placeholder="Optional" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-15000">Unter 15.000€</SelectItem>
                            <SelectItem value="15000-25000">15.000€ - 25.000€</SelectItem>
                            <SelectItem value="25000-35000">25.000€ - 35.000€</SelectItem>
                            <SelectItem value="over-35000">Über 35.000€</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}

                {/* Schritt 3 */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Installationsadresse</h3>
                    
                    {/* Honeypot - versteckt für Menschen, sichtbar für Bots */}
                    <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                      <Input
                        type="text"
                        name="website_url"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="street">Straße und Hausnummer <span className="text-red-500">*</span></Label>
                      <Input
                        id="street"
                        type="text"
                        placeholder="z.B. Musterstraße 42"
                        value={formData.street}
                        onChange={(e) => handleInputChange('street', e.target.value)}
                        className={`mt-1 ${errors.street ? 'border-red-500 border-2' : ''}`}
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="postalCode">Postleitzahl <span className="text-red-500">*</span></Label>
                        <Input
                          id="postalCode"
                          type="text"
                          placeholder="z.B. 01234"
                          value={formData.postalCode}
                          onChange={(e) => handleInputChange('postalCode', e.target.value)}
                          className={`mt-1 ${errors.postalCode ? 'border-red-500 border-2' : ''}`}
                          maxLength={5}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="city">Ort <span className="text-red-500">*</span></Label>
                        <Input
                          id="city"
                          type="text"
                          placeholder="z.B. Dresden"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          className={`mt-1 ${errors.city ? 'border-red-500 border-2' : ''}`}
                        />
                      </div>
                    </div>
                    
                    {/* Mathe-CAPTCHA */}
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <Label htmlFor="captcha" className="text-gray-700 font-medium">
                        Sicherheitsfrage: Was ist {captchaNumbers.a} + {captchaNumbers.b}? <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="captcha"
                        type="number"
                        placeholder="Ihre Antwort"
                        value={captchaAnswer}
                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                        className={`mt-2 w-32 ${errors.captcha ? 'border-red-500 border-2' : ''}`}
                        data-testid="captcha-input"
                      />
                    </div>
                  </div>
                )}

                {/* Navigation Buttons - sticky auf Mobile */}
                <div className="flex justify-between pt-6 border-t sticky bottom-0 bg-white pb-4 md:pb-0 md:relative">
                  {currentStep > 1 && (
                    <Button
                      type="button"
                      onClick={prevStep}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Zurück</span>
                    </Button>
                  )}
                  
                  <div className="ml-auto">
                    {currentStep < totalSteps ? (
                      <Button
                        type="button"
                        onClick={nextStep}
                        disabled={isSubmitting}
                        className="bg-brand-green hover:bg-brand-green-hover text-white flex items-center gap-2"
                      >
                        Weiter
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={nextStep}
                        disabled={isSubmitting}
                        className="bg-brand-orange hover:bg-brand-orange-hover text-white flex items-center gap-2"
                      >
                        {isSubmitting ? 'Wird gesendet...' : 'Anfrage absenden'}
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ModalContactForm;
